import { useCallback, useEffect, useRef, useState } from "react";
import { Search, X, Loader2, MapPin } from "lucide-react";
import { usePlaceSearch } from "@/hooks/usePlaceSearch";
import { cn } from "@/lib/utils";

// Location search over the fleet map — the same "City, Neighborhood, ZIP,
// Address" box Bouncie's own map has. Type, pick a suggestion (or just press
// Enter), and the map moves there with a marker on the hit.
//
// All the Places/Geocoder plumbing lives in usePlaceSearch (shared with the
// facility address field); what's left here is the overlay UI and the
// map-specific "fly there and drop a marker" behaviour.

const POINT_ZOOM = 16; // a street address has no viewport of its own to fit

export function MapSearch({ maps, map, className }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  const [highlight, setHighlight] = useState(-1);

  const rootRef = useRef(null);
  const markerRef = useRef(null);

  // A live getter, not a snapshot: bias toward whatever is on screen at the
  // moment the request goes out.
  const bias = useCallback(() => map?.getBounds?.() || null, [map]);
  const { suggestions, resolve, resolving, suppressNext, clearSuggestions } =
    usePlaceSearch({ maps, query, bias });

  useEffect(() => {
    setHighlight(-1);
    if (suggestions.length) setOpen(true);
  }, [suggestions]);

  const clearMarker = useCallback(() => {
    markerRef.current?.setMap(null);
    markerRef.current = null;
  }, []);

  useEffect(() => () => { markerRef.current?.setMap(null); }, []);

  // Close the dropdown when the click lands anywhere else on the page.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const reveal = useCallback((hit) => {
    if (!map || !maps) return;
    if (hit.viewport) {
      // A city/ZIP knows its own extent — fit that rather than guessing a zoom.
      map.fitBounds(hit.viewport, 40);
    } else {
      map.panTo(hit.location);
      map.setZoom(Math.max(map.getZoom() || 0, POINT_ZOOM));
    }
    clearMarker();
    markerRef.current = new maps.Marker({
      position: hit.location, map, clickable: false, zIndex: 900,
      title: hit.address,
      icon: {
        path: maps.SymbolPath.CIRCLE, scale: 7,
        fillColor: "#38bdf8", fillOpacity: 1,
        strokeColor: "#0a0a0b", strokeWeight: 2,
      },
    });
  }, [map, maps, clearMarker]);

  const run = useCallback((request) => {
    setNotice(null);
    resolve(request).then(reveal).catch((err) => setNotice(err.message));
  }, [resolve, reveal]);

  const pick = useCallback((suggestion) => {
    suppressNext();
    setQuery(suggestion.description);
    clearSuggestions();
    setOpen(false);
    // Resolve through the geocoder by place id rather than PlacesService, so
    // the pick needs nothing beyond the core API.
    run({ placeId: suggestion.placeId });
  }, [run, suppressNext, clearSuggestions]);

  const submit = useCallback(() => {
    const q = query.trim();
    if (!q) return;
    setOpen(false);
    const request = { address: q };
    const bounds = map?.getBounds?.();
    if (bounds) request.bounds = bounds;
    run(request);
  }, [query, map, run]);

  const reset = () => {
    setQuery("");
    clearSuggestions();
    setOpen(false);
    setNotice(null);
    setHighlight(-1);
    clearMarker();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" && suggestions.length) {
      e.preventDefault();
      setOpen(true);
      setHighlight((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp" && suggestions.length) {
      e.preventDefault();
      setHighlight((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && highlight >= 0 && suggestions[highlight]) pick(suggestions[highlight]);
      else submit();
    } else if (e.key === "Escape") {
      if (open) setOpen(false);
      else reset();
    }
  };

  return (
    <div
      ref={rootRef}
      data-testid="map-search"
      // Sits above the map's own controls; width backs off on narrow maps so
      // it never runs under the zoom buttons on the opposite corner.
      className={cn("absolute left-3 top-3 z-20 w-[min(20rem,calc(100%-5.5rem))]", className)}
    >
      <div className="flex items-center gap-2 rounded-xl border border-border/70 bg-card/95 px-2.5 shadow-lg backdrop-blur">
        {resolving
          ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
          : <Search className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <input
          data-testid="map-search-input"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setNotice(null); }}
          onFocus={() => { if (suggestions.length) setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder="City, Neighborhood, ZIP, Address"
          aria-label="Search the map for a place"
          className="h-9 w-full bg-transparent text-xs text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
        {query && (
          <button
            data-testid="map-search-clear"
            onClick={reset}
            title="Clear search"
            className="shrink-0 text-muted-foreground transition hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <ul
          data-testid="map-search-results"
          className="mt-1.5 overflow-hidden rounded-xl border border-border/70 bg-card/95 shadow-lg backdrop-blur"
        >
          {suggestions.map((s, i) => (
            <li key={s.placeId}>
              <button
                onClick={() => pick(s)}
                onMouseEnter={() => setHighlight(i)}
                className={cn(
                  "flex w-full items-start gap-2 px-2.5 py-2 text-left transition",
                  i === highlight ? "bg-secondary/60" : "hover:bg-secondary/40"
                )}
              >
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0">
                  <span className="block truncate text-xs font-medium text-foreground">{s.primary}</span>
                  {s.secondary && (
                    <span className="block truncate text-[11px] text-muted-foreground">{s.secondary}</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {notice && (
        <p className="mt-1.5 rounded-lg border border-border/70 bg-card/95 px-2.5 py-1.5 text-[11px] text-muted-foreground shadow-lg backdrop-blur">
          {notice}
        </p>
      )}
    </div>
  );
}
